import React from 'react';

import { observer } from 'mobx-react';

import { Tooltip } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { Button } from 'components/Button';

import GroupTypeChip from 'components/Chips/GroupTypeChip';

import ClampedText from 'components/ClampedText';

import { useStore } from 'utils/hooks/store';

import { isDynamicGroup } from 'containers/MemberGroups/utils/membership';

import { RuleSummary, useRuleFieldData } from '../components/RuleBuilder';
import EditMemberGroupDetails from './EditMemberGroupDetails';
import ConvertToDynamic from './ConvertToDynamic';
import {
  DetailList,
  DetailRow,
  DetailLabel,
  DetailLabelWithHint,
  DetailValue,
  RuleRow,
  Section,
  SectionBody,
  SectionHeader,
  SectionTitle,
} from './styles';

const MEMBERSHIP_RULE_HINT = 'Membership updates automatically as users’ details change.';
const DESCRIPTION_CLAMP_LINES = 6;

const MemberGroupDetailsCard: React.FunctionComponent = observer(() => {
  const { memberGroupDetails } = useStore();
  const { group, activeRule, editing, converting } = memberGroupDetails;
  const isDynamic = group ? isDynamicGroup(group) : false;
  const needsFieldData = isDynamic && !editing && !converting;
  const { customFieldLabels } = useRuleFieldData(needsFieldData);

  if (!group) {
    return null;
  }

  if (converting) {
    return <ConvertToDynamic onExit={memberGroupDetails.stopConverting} />;
  }

  if (editing) {
    return <EditMemberGroupDetails setEditing={memberGroupDetails.setEditing} />;
  }

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Details</SectionTitle>
        <Button
          size="medium"
          variant="text"
          startIcon={<EditIcon fontSize="small" />}
          onClick={() => memberGroupDetails.setEditing(true)}
        >
          Edit
        </Button>
      </SectionHeader>
      <SectionBody>
        <DetailList>
          <DetailRow>
            <DetailLabel>Membership type</DetailLabel>
            <div data-testid="group-type-chip">
              <GroupTypeChip type={group.membershipMode} />
            </div>
          </DetailRow>
          {isDynamic && activeRule && (
            <DetailRow>
              <DetailLabelWithHint>
                Membership rule
                <Tooltip title={MEMBERSHIP_RULE_HINT} arrow>
                  <InfoOutlinedIcon />
                </Tooltip>
              </DetailLabelWithHint>
              <RuleRow data-testid="group-rule-summary">
                <RuleSummary rule={activeRule} customFieldLabels={customFieldLabels} />
              </RuleRow>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>Description</DetailLabel>
            {group.description ? (
              <ClampedText lines={DESCRIPTION_CLAMP_LINES} whiteSpace="pre-wrap">
                {group.description}
              </ClampedText>
            ) : (
              <DetailValue>—</DetailValue>
            )}
          </DetailRow>
        </DetailList>
      </SectionBody>
    </Section>
  );
});

export default MemberGroupDetailsCard;
